
import {
  defineComponent,
  computed,
  onMounted,
  onUnmounted,
  reactive,
  ref
} from "vue";
import { PAGE_ID } from "@/const/log-const";
import CommonRecipeList from "@/components/commonRecipe/CommonRecipeList.vue";
import SearchInputView from "@/components/search/SearchInputView.vue";
import SearchDropdownList from "@/components/search/SearchDropdownList.vue";
import SearchRefinedTagList from "@/components/search/SearchRefinedTagList.vue";
import SearchApplianceIconList from "@/components/search/SearchApplianceIconList.vue";
import CommonDialog from "@/components/common/CommonDialog.vue";
import store from "@/store";
import router from "@/router";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { BeaconLogInfo } from "@/types/common";
import { Recipe } from "@/types/recipes";
import {
  SEARCH_SORT_TYPE,
  RequestSearchKeyword,
  SearchTag,
  ApplianceData
} from "@/types/search";
import { SearchApplianceList, Products, Product } from "@/types/common";
import { DropdownList } from "@/types/dropdown";
import { getViewedItemsString, setSearchHistoryKeyword } from "@/utils/search-util";
import NativeClient, { TitleInfo } from "@/client/native-client";
import { createRecipeToCommonRecipes } from "@/utils/search-util";
import CommonUtil from "@/utils/common-utils";

/** スクロールイベントのIF */
interface HTMLElementEvent<T extends HTMLElement> extends Event {
  target: T;
}

export default defineComponent({
  name: "SearchPage",
  components: {
    CommonRecipeList,
    SearchInputView,
    SearchDropdownList,
    SearchRefinedTagList,
    SearchApplianceIconList,
    CommonDialog
  },
  setup() {
    const sortDropdownList: DropdownList = {
      list: [
        {
          label: "おすすめ順",
          type: SEARCH_SORT_TYPE. MATCH,
          id: 1
        },
        {
          label: "新着順",
          type: SEARCH_SORT_TYPE.RECENT,
          id: 2
        }
      ]
    };
    const refinedTags = ref(Array<SearchTag>());
    const titleInfo = new TitleInfo(false, "検索結果");
    titleInfo.rightIcon = true;
    NativeClient.setTitle(titleInfo);
    CommonUtil.scroll({ top: 0, left: 0 });
    /**
     * レシピ検索中制御
     */
    const state = reactive<{ isSearchingRecipes: boolean }>({
      // レシピ検索中フラグ
      isSearchingRecipes: false
    });
    // urlから選択されたkeywordを取得
    const keyword = ref(String(router.currentRoute.value.query.keyword));
    const totalCount = ref(Number(0));
    const recipeCount = ref(Number(0));
    const sortType = ref(String(router.currentRoute.value.query.sort));
    const recipes = ref();
    const searchApplianceList = ref(new SearchApplianceList());
    const applianceList = ref(Array<ApplianceData>());
    const actionLog = ref("初回");
    const isMounted = ref(false);
    keyword.value = decodeURIComponent(keyword.value);
    const setQuery = async () => {
      const loadKeyword = store.getters["search/getKeyword"];
      const loadSortType = store.getters["search/getSortType"];
      const loadTagsText = store.getters["search/getTags"];
      NativeClient.sendDebugLog(
        "SearchPage setQuery loadKeyword=" +
          loadKeyword +
          " loadSortType=" +
          loadSortType +
          " loadTags=" +
          loadTagsText
      );
      if (loadKeyword != null) {
        keyword.value = loadKeyword;
      }
      if (loadSortType != null) {
        sortType.value = loadSortType;
      }
      if (loadTagsText != null) {
        refinedTags.value = JSON.parse(loadTagsText);
      } else {
        refinedTags.value = JSON.parse(
          String(router.currentRoute.value.query.tags)
        );
      }
    };
    setQuery();

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      NativeClient.sendDebugLog("SearchPage updateDisplay");
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    const sendLogAction = (message: string, start: number) => {
      if (!isMounted.value) {
        return;
      }
      const viewedItem = Array<string>();
      // 1つ目のログを設定
      viewedItem.push(message);
      // 検索Hit数
      let count = 0;
      if (totalCount.value != null) {
        count = totalCount.value;
      }
      viewedItem.push(count + "件");
      // 表示件数
      let displayCount = "0件〜0件";
      if (count != 0) {
        let size = 0;
        if (recipes.value != null) {
          size = recipes.value.length;
        }
        displayCount = (start+1) + "件〜" + size + "件";
      }
      viewedItem.push(displayCount);
      // ソート情報
      let type = SEARCH_SORT_TYPE.MATCH;
      let sortValue = "";
      if (sortType.value != null) {
        type = sortType.value;
      }
      for (const item of sortDropdownList.list) {
        if (item.type == type) {
          sortValue = item.label;
        }
      }
      viewedItem.push(sortValue);
      // 検索ワード
      viewedItem.push(keyword.value);
      // 検索対象
      let appliance = "";
      for (const item of applianceList.value) {
        if (appliance.length != 0) {
          appliance += ",";
        }
        if (item.check) {
          appliance += item.product.productCode;
        }
      }
      appliance = "{" + appliance + "}";
      viewedItem.push(appliance);
      // 選択タグの設定
      const tagMap = new Map([[String.prototype, [String.prototype]]]);
      NativeClient.sendDebugLog("sendLogAction refinedTags.value=" + JSON.stringify(refinedTags.value));
      if (refinedTags.value != null) {
        for (const searchTag of refinedTags.value) {
          let list = tagMap.get(searchTag.description);
          if (list == null) {
            list = Array<string>();
          }
          list.push(searchTag.name);
          tagMap.set(searchTag.description, list);
        }
        for (const key of tagMap.keys()) {
          if (key == "") {
            continue;
          }
          const value = tagMap.get(key);
          let tagItem = "";
          if (value != null) {
            for (const tag of value) {
              if (tagItem.length != 0) {
                tagItem += ",";
              }
              tagItem += tag;
            }
          }
          tagItem = "{" + tagItem + "}";
          tagItem = key + ":" + tagItem;
          viewedItem.push(tagItem);
        }
      }
      const items = getViewedItemsString(viewedItem);
      const itemList = Array<string>();
      itemList.push(items);
      const beaconLogInfo = new BeaconLogInfo(PAGE_ID.SEARCH_RESULT, "", itemList);
      NativeClient.sendClickedActionLogViewedItemSingleLine(beaconLogInfo);
    };

    const getSearch = async () => {
      return new Promise(resolve => {
        NativeClient.sendDebugLog("SearchPage getSearch");
        let applianeText = "";
        NativeClient.sendDebugLog(
          "SearchPage getSearch searchApplianceList=" +
            searchApplianceList.value
        );
        for (
          let i = 0;
          i < searchApplianceList.value.applianceList.length;
          i++
        ) {
          const item = searchApplianceList.value.applianceList[i];
          if (item.check) {
            if (applianeText != "") {
              applianeText += " ";
            }
            applianeText += item.productCode;
          }
        }
        const payload: RequestSearchKeyword = {
          keyword: keyword.value,
          sort: sortType.value,
          appliance: applianeText,
          tags: refinedTags.value,
        };
        Promise.all([
          store.dispatch("search/searchKeywordRecipesAndTags", payload)
        ]).then(result => {
          resolve(true);
        });
      });
    };
    /**
     * 検索結果レシピリストを取得する
     */
    const getRecipes = async () => {
      console.trace();
      NativeClient.sendDebugLog("SearchPage getRecipes");
      // 検索履歴非表示
      // setSearchHistoryKeyword(keyword.value);
      store.dispatch("common/startLoading");
      await Promise.all([NativeClient.getApiParams()]).then(result => {
        const apiParams = store.getters["common/getApiBaseParams"];
        NativeClient.sendDebugLog(
          "SearchPage getRecipes apiParams=" + JSON.stringify(apiParams)
        );
        if (apiParams.token) {
          Promise.all([
            NativeClient.getSearchAppliance(),
            store.dispatch("search/getProducts")
          ]).then(result => {
            NativeClient.sendDebugLog("SearchPage getRecipes request");
            // getSearchでStopLoadingしている
            Promise.all([getSearch()]).then(result => {
              store.dispatch("common/stopLoading");
              updateDisplay();
              state.isSearchingRecipes = false;
            });
          });
        } else {
          NativeClient.sendDebugLog("SearchPage getRecipes request");
          Promise.all([getSearch()]).then(result => {
            store.dispatch("common/stopLoading");
            updateDisplay();
            state.isSearchingRecipes = false;
          });
        }
      });
    };

    const getSearchAppliance = (
      products: Array<Product>,
      loadApplianceList: string
    ) => {
      if (loadApplianceList != null) {
        searchApplianceList.value = JSON.parse(loadApplianceList);
      }
      NativeClient.sendDebugLog(
        "SearchPage getSearchAppliance param=" +
          JSON.stringify(searchApplianceList.value)
      );
      if (loadApplianceList == null || loadApplianceList == "") {
        return;
      }
      for (let i = 0; i < searchApplianceList.value.applianceList.length; i++) {
        const item = searchApplianceList.value.applianceList[i];
        for (let j = 0; j < products.length; j++) {
          const product = products[j];
          if (!item.check) {
            continue;
          }
          if (
            product.uniqueId == item.uniqueId &&
            ((product.geminiId != null &&
              product.geminiId != "" &&
              product.geminiId == item.geminiId) ||
              (product.applianceId != null &&
                product.applianceId != "" &&
                product.applianceId == item.applianceId))
          ) {
            applianceList.value.push(new ApplianceData(j, true, product));
            break;
          }
        }
      }
      NativeClient.sendDebugLog(
        "SearchPage computed applianceList=" + JSON.stringify(applianceList)
      );
      return applianceList;
    };

    const products = computed(() => {
      const result: Products | null = store.getters["search/getProducts"];
      const loadApplianceList = store.getters["common/getSearchAppliance"];
      applianceList.value = [];
      if (result != null && result.products != null) {
        getSearchAppliance(result.products, loadApplianceList);
        NativeClient.sendDebugLog(
          "SearchPage computed create applianceList=" +
            JSON.stringify(applianceList)
        );
        return applianceList;
      }
      NativeClient.sendDebugLog("SearchPage computed products empty");
      return null;
    });

    /**
     * 検索結果レシピリストの取得を実行する
     */
    const execute = async () => {
      getRecipes();
    };

    /**
     * コンポーネントがマウントされたときに通知される
     */
    onMounted(() => {
      const beaconLogInfo = new BeaconLogInfo(PAGE_ID.SEARCH_RESULT, null, new Array<string>());
      NativeClient.sendViewedLog(beaconLogInfo);
      isMounted.value = true;
      execute();
    });

    /**
     * コンポーネントがマウントされたときに通知される
     */
    onUnmounted(async () => {
      // 画面から離れた場合、stateに保持したレシピ一覧をクリア
      await store.dispatch("search/updateSearchKeywordRecipes", null);
      isMounted.value = false;
    });

    /** APIリクエスト時のエラーハンドル */
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();

    /** ダイアログの制御 */
    const { closeDialog } = useApiErrorDialog();

    // 検索結果レシピリストを取得する
    const searchRecipes = computed(() => {
      const result = store.getters["search/getSearchKeywordRecipes"];
      const searchUpdateLog = actionLog.value;
      NativeClient.sendDebugLog("searchRecipes result=" + JSON.stringify(result));
      if (result != null && result.recipes != null) {
        totalCount.value = result.totalCount;
        recipeCount.value = result.recipes.length;
        if (result && result.recipes) {
          const recipeIds: Array<number> = result.recipes.map(
            (recipe: Recipe) => recipe.recipeId
          );
        }
        const commonRecipe = createRecipeToCommonRecipes(result.recipes);
        NativeClient.sendDebugLog(
          "SearchPage computed recommendRecipes " +
            JSON.stringify(result.recipes)
        );
        recipes.value = commonRecipe;
        if (!(searchUpdateLog == "更新" && result.start == 0)) {
          sendLogAction(searchUpdateLog, result.start);
        }
        return commonRecipe;
      }
      totalCount.value = 0;
      recipeCount.value = 0;
      recipes.value = null;
      if (result) {
        sendLogAction(searchUpdateLog, 0);
      }
      return null;
    });

    const initSearchRecipe = () => {
      recipeCount.value = 0;
      totalCount.value = 0;
      store.dispatch("search/updateSearchKeywordRecipes", null);
    }
    /**
     * 選択されたソートを設定する
     */
    const selectSort = (value: string) => {
      NativeClient.sendDebugLog("SearchPage selectSort value=" + value);
      sortType.value = value;
      actionLog.value = "ソート";
      // 検索結果を削除してスクロール分を初期化
      initSearchRecipe();
      getRecipes();
    };

    /**
     * レシピカテゴリー取得APIの呼び出しをリトライする
     */
    const retryGetRecipes = () => {
      // ダイアログを閉じる
      closeDialog();
      getRecipes();
    };

    /**
     * 選択されたレシピをNativeに通知する
     */
    const selectRecipe = (id: number, type: string) => {
      // Native側にレシピ詳細画面に遷移指示を出す
      NativeClient.sendDebugLog(
        "SearchPage selectRecipe id=" + id + " type=" + type
      );
      NativeClient.transitionReceipDetail(id, type);
    };

    /**
     * 検索タグを設定する
     */
    const selectRefinedTag = (alias: string, id: number) => {
      for (let i = 0; i < refinedTags.value.length; i++) {
        const tag = refinedTags.value[i];
        if (tag.alias == alias && tag.id == id) {
          refinedTags.value.splice(i, 1);
          break;
        }
      }
      actionLog.value = "フィルタ変更";
      // 検索結果を削除してスクロール分を初期化
      initSearchRecipe();
      getRecipes();
    };

    /**
     * レシピリストスクロール時イベント
     */
    const onScroll = (e: HTMLElementEvent<HTMLDivElement>) => {
      // NativeClient.sendDebugLog(
      //   "SearchPage onScroll top=" +
      //     e.target.scrollTop +
      //     " height=" +
      //     e.target.offsetHeight +
      //     " total=" +
      //     (e.target.scrollTop + e.target.offsetHeight) +
      //     " scrollH=" +
      //     e.target.scrollHeight
      // );
      // データ初期化後のscrollTop=0では読み込まないようにする
      if (
        e.target.scrollTop != 0 &&
        e.target.scrollTop + e.target.offsetHeight >=
        e.target.scrollHeight - 1 &&
        recipeCount.value < totalCount.value &&
        !state.isSearchingRecipes
      ) {
        NativeClient.sendDebugLog("SearchPage onScroll totalCount = " + totalCount.value + " recipeCount = " + recipeCount.value);
        state.isSearchingRecipes = true;
        actionLog.value = "更新";
        // レシピリスト取得
        getRecipes();
      }
    };

    /**
     * 検索結果の表示件数を取得する
     */
    const getCount = () => {
      let size = 0;
      if (recipes.value != null) {
        size = recipes.value.length;
      }
      let result = "";
      if (totalCount.value == 0) {
        result = "0件";
      } else {
        result = "" + totalCount.value + "件中" + "1-" + size + "件";
      }
      return result;
    };

    const onClickSearchChange = () => {
      const searchTagsText = JSON.stringify(refinedTags.value);
      store.dispatch("search/updateTags", searchTagsText);
      const encodeKeyword = encodeURIComponent(keyword.value);
      NativeClient.sendDebugLog("SearchPage onClickSearchChange keyword=" + keyword.value + " encodeKeyword=" + encodeKeyword)
      router.push(
        "search-detail?top=0&sort=" +
          sortType.value +
          "&keyword=" +
          encodeKeyword
      );
    };
    /**
     * 検索キーワードの入力フォーカスを設定する
     */
    const setFocusInputView = () => {
      onClickSearchChange();
    };

    const getKeyword = () => {
      return keyword.value;
    };

    const getTopViewHeight = () => {
      return refinedTags.value.length != 0 ? "24rem" : "20.6rem";
    };

    const getListMerginHeight = () => {
      return refinedTags.value.length != 0 ? "28rem" : "24.7rem";
    };

    return {
      searchRecipes,
      selectRecipe,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      closeDialog,
      getRecipes,
      retryGetRecipes,
      keyword,
      totalCount,
      onScroll,
      selectSort,
      sortType,
      sortDropdownList,
      selectRefinedTag,
      getCount,
      setFocusInputView,
      getKeyword,
      onClickSearchChange,
      refinedTags,
      products,
      applianceList,
      getTopViewHeight,
      getListMerginHeight,
      updateDisplayKey
    };
  }
});
